declare global {
  interface Window {
    dataLayer?: unknown[];
  }
}

type AnalyticsEventParams = Record<string, unknown>;

const getGtmContainerId = () => {
  return import.meta.env.VITE_GTM_CONTAINER_ID as string | undefined;
};

const ensureDataLayer = () => {
  if (!window.dataLayer) {
    window.dataLayer = [];
  }
  return window.dataLayer;
};

const injectGtmScript = (containerId: string) => {
  const existingScript = document.querySelector<HTMLScriptElement>(
    `script[src*="googletagmanager.com/gtm.js?id=${containerId}"]`
  );
  if (existingScript) {
    return;
  }

  const dl = ensureDataLayer();
  dl.push({
    'gtm.start': Date.now(),
    event: 'gtm.js',
  });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${containerId}`;
  document.head.appendChild(script);
};

export const initializeGtm = () => {
  if (typeof window === 'undefined') return;

  const containerId = getGtmContainerId();
  if (!containerId) {
    return;
  }

  const run = () => injectGtmScript(containerId);

  // Defer until after load + idle so tag work does not compete with LCP / first paint.
  const schedule = () => {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(run, { timeout: 6000 });
    } else {
      window.setTimeout(run, 1);
    }
  };

  if (document.readyState === 'complete') schedule();
  else window.addEventListener('load', schedule, { once: true });
};

export const pushToDataLayer = (event: string, params: AnalyticsEventParams = {}) => {
  if (typeof window === 'undefined') return;
  const dl = ensureDataLayer();
  dl.push({
    event,
    ...params,
  });
};

export const trackPageView = (page: {
  path: string;
  title: string;
  page_type:
    | 'landing'
    | 'calculator'
    | 'results'
    | 'about'
    | 'contact'
    | 'privacy'
    | 'faq'
    | 'feedback'
    | 'learn_hub'
    | 'learn_compare'
    | 'learn_scenarios'
    | 'learn_guides'
    | 'learn_article'
    | 'province_calculator';
}) => {
  const pageUrl = new URL(window.location.href);
  pageUrl.searchParams.delete('s');
  pushToDataLayer('page_view', {
    page_location: pageUrl.toString(),
    page_path: page.path,
    page_title: page.title,
    page_type: page.page_type,
  });
};

export const trackEvent = (name: string, params: AnalyticsEventParams = {}) => {
  pushToDataLayer(name, params);
};

